import React, { forwardRef, useCallback, useRef, useState } from "react";
import styles from "./Button.module.css";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";

interface ButtonProps {
  type?: "primary" | "secondary" | "text" | "danger" | "inverse" | "link" | "black" | "textButton";
  onClick?: (e) => void;
  onMouseDown?: (e) => void;
  onMouseUp?: (e) => void;
  size?: "small" | "medium" | "large";
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  disableWhileOnClick?: boolean;
  htmlType?: "button" | "reset" | "submit";
  style?: any;
  block?: boolean;
}

export default forwardRef(function Button(props: ButtonProps, forwardRef) {
  const { 
    type = "primary",
    className = "",
    size = "medium",
    htmlType = "button",
    disabled = false,
    loading: loadingProp = false,
    disableWhileOnClick = false,
    block = false,
    children,
    onClick,
    onMouseDown,
    onMouseUp,
    ...rest
  } = props;

  const [loading, setLoading] = useState(false);

  const asyncEvent = (onEvent?: (e) => void) => useCallback(async (e) => {
    setLoading(true);
    try {
      await onEvent?.(e);
    } finally {
      setLoading(false);
    }
  }, [onEvent]);

  const buttonRef = useRef(null);
  const _onClick = asyncEvent(onClick);
  const _onMouseDown = asyncEvent(onMouseDown);

  const updateRefs = el => {
    buttonRef.current = el;
    if (forwardRef) {
      if (forwardRef instanceof Function) {
        forwardRef(el);
      } else {
        forwardRef.current = el;
      }
    }
  };

  return (
    <button
      ref={updateRefs}
      type={htmlType}
      disabled={disabled || loading || loadingProp}
      className={`${styles.button} ${styles[type]} ${styles[size]} ${className} ${block ? styles.block : ""}`}
      onClick={disableWhileOnClick ? _onClick : onClick}
      onMouseDown={disableWhileOnClick ? _onMouseDown : onMouseDown}
      onMouseUp={onMouseUp}
      {...rest}
    >
      {(loading || loadingProp) && (
        <LoadingSpinner
          size="1.5rem"
          style={{ position: "absolute" }}
          color="inherit"
        />
      )}
      <div style={{display: "contents", visibility: (loading || loadingProp) ? "hidden" : "inherit"}}>{children}</div>
    </button>
  );
});
